import { IFileAttachment } from '../fileTypes';
import {
  ITQProfileCompletionQuery,
  TQRatingCategoryValue,
} from './';

export interface ITQProfileCompletionResponse {
  questionId: string;
  selectedOptions?: string[];
  subjectiveAnswer?: string;
  question: string;
  order: number;
}

export interface ITQProfileInfo {
  hasAccess: boolean;
  tqConnectScore: number;
  tqConnectBadge: string;
  tqConnectProfileCompletionResponses: ITQProfileCompletionResponse[];
  tqReviewReceived: number;
  tqReviewGiven: number;
  tqShoutoutReceived: number;
  tqShoutoutGiven: number;
}

export enum UserProfileTabItemType {
  RECEIVED = 'received',
  GIVEN = 'given',
}

export type UserProfileTabItemTypeValue = `${UserProfileTabItemType}`;

export interface IUserReviewsFilters {
  type: UserProfileTabItemTypeValue;
  rating: TQRatingCategoryValue | null;
}

export interface IUserShoutoutsFilters {
  type: UserProfileTabItemTypeValue;
}

export interface ITQProfileCompletionResponseRequest {
  questionId: string;
  selectedOptions?: string[];
  subjectiveAnswer?: string;
}

export interface ITQProfileCompletionResult {
  tqConnectProfileCompletionResponses: ITQProfileCompletionResponse[];
  assignedTQScore: number;
  isTQScoreEvaluated: boolean;
}

export interface ITQProfileCompletionState {
  tqConnectProfileCompletionResponses: ITQProfileCompletionQueryWithResponse[];
  assignedTQScore: number;
  isTQScoreEvaluated: boolean;
}

export interface ITQProfileCompletionQueryWithResponse
  extends ITQProfileCompletionQuery,
    ITQProfileCompletionResponse {}

export enum TQConnectUserAccess {
  CREATOR = 'creator',
  CUSTOMER = 'customer',
}

export type TQConnectUserAccessType = `${TQConnectUserAccess}`;

// Profile Reviews Types
export interface ITQProfileReviewsStatistics
  extends Record<TQRatingCategoryValue, number> {}

export interface ITQProfileReviewGivenByUser {
  _id: string;
  name: string;
  profilePicUrl: string;
  tqConnectScore: number;
  tqConnectBadge: string;
}

export interface ITQProfileReview {
  rating: TQRatingCategoryValue;
  feedback: string;
  attachments?: IFileAttachment[];
  date: string;
  givenBy: ITQProfileReviewGivenByUser | null;
  respondedTo: ITQProfileReviewServiceRequest | null;
}

export interface ITQProfileReviewServiceRequest {
  _id: string;
  title: string;
}

export interface ITQProfileReviewsQuery {
  limit?: number;
  page?: number;
  rating?: TQRatingCategoryValue;
  type?: UserProfileTabItemTypeValue;
  queryForUser?: string;
}

export interface ITQProfileReviewsResult {
  statistics: ITQProfileReviewsStatistics;
  data: ITQProfileReview[];
  total: number;
}

// Shoutouts Types
export interface ITQShoutoutCategoryDetails {
  id: string;
  name: string;
  logo: string;
}

export interface ITQShoutoutChannel {
  _id: string;
  name: string;
}

export interface ITQShoutoutUser {
  _id: string;
  name: string;
  profilePicUrl: string;
}

export enum TQShoutoutVisibility {
  PROFILE_ONLY = 'profile-only',
  CHANNEL_ONLY = 'channel-only',
}

export type TQShoutoutVisibilityType = `${TQShoutoutVisibility}`;

export interface ITQShoutout {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  description: string;
  category: ITQShoutoutCategoryDetails;
  coverImage: string;
  visibility: TQShoutoutVisibilityType;
  channels?: ITQShoutoutChannel[];
  from: ITQShoutoutUser;
  to: ITQShoutoutUser;
}

export interface ITQShoutoutsQuery {
  limit?: number;
  page?: number;
  type?: UserProfileTabItemTypeValue;
  visibility?: TQShoutoutVisibilityType;
  queryForUser?: string;
}

export interface ITQShoutoutsResult {
  data: ITQShoutout[];
  total: number;
}

export interface ICreateTQShoutoutRequest {
  to: string;
  description: string;
  category: ITQShoutoutCategoryDetails | null;
  coverImage: string;
  visibility: TQShoutoutVisibilityType;
  channels?: string[];
}

export interface IShoutoutModalState {
  isOpen: boolean;
  givenBy?: ITQShoutoutUser;
  givenTo?: ITQShoutoutUser;
  shoutout?: Partial<ITQShoutout>;
  callback?: () => void;
}
